import { Country } from '@tiendanube/common/src/enums';
import { expirationDateRegex } from 'domains/Billing/Checkout/hooks/utils';

export const handleExpirationDate = (
  value: string,
  setFieldValue: (field: string, value: string) => void,
) => {
  if (value.length > 2 && value[2] !== '/')
    value = value.slice(0, 2) + '/' + value.slice(2);

  if (!expirationDateRegex.test(value)) return;

  setFieldValue('expirationDate', value);
  if (value.length === 5) {
    setFieldValue('expirationMonth', value.slice(0, 2));
    setFieldValue('expirationYear', value.slice(3));
  }
};

export const handleIndentificationType = (
  value: string,
  setFieldValue: (field: string, value: string) => void,
) => {
  setFieldValue('identificationType', value);
  setFieldValue('identificationNumber', '');
  setFieldValue('identificationNumberParsed', '');
};

const ID_NUMBER_PLACEHOLDER_MAP = {
  DNI: '12.345.678',
  CNPJ: '00.000.000/0000-00',
  CC: '123.456.789',
  CE: '',
  RUT: '00.000.000-K',
  NIT: '000.000.000-0',
  Otro: '',
  CPF: '000.000.000-00',
  Cedula: '1.234.567',
  LC: '1.234.567',
  LE: '1.234.567',
  CI: '1.234.567',
};

const ID_NUMBER_MASK_MAP = {
  DNI: { defaultMask: '9.999.999', 8: '99.999.999' },
  CNPJ: { defaultMask: '99.999.999/9999-99' },
  CC: { defaultMask: '999.999.9999' },
  CE: { defaultMask: '999999999999999', allowLetters: true },
  RUT: { defaultMask: '99.999.999-9', allowLetters: true },
  NIT: { defaultMask: '99999999999999999999', allowLetters: true },
  Otro: { defaultMask: '99999999999999999999', allowLetters: true },
  CPF: { defaultMask: '999.999.999-99' },
  Cedula: { defaultMask: '9.999.999' },
  LC: { defaultMask: '9.999.999' },
  LE: { defaultMask: '9.999.999' },
  CI: { defaultMask: '9.999.999' },
};

const DEFAULT_IDENTIFICATION_TYPE_FOR_COUNTRY: Partial<
  Record<Country, string>
> = {
  [Country.AR]: 'DNI',
  [Country.BR]: 'CPF',
  [Country.CO]: 'CC',
  [Country.CL]: 'RUT',
};

export const getIdentificationNumberPlaceholder = (
  country: string,
  identificationType: string,
) =>
  ID_NUMBER_PLACEHOLDER_MAP[
    identificationType || DEFAULT_IDENTIFICATION_TYPE_FOR_COUNTRY[country]
  ];

export const cleanString = (value: string, allowLetters: boolean) =>
  allowLetters ? value.replace(/[^a-zA-Z0-9]/g, '') : value.replace(/\D/g, '');

const fitToMask = (
  cleanValue: string,
  mask: string,
  restrictLength: boolean,
) => {
  let parsedValue = '';
  let cleanValueIndex = 0;

  for (let i = 0; cleanValueIndex < cleanValue.length; i++) {
    if (
      (i < mask.length && mask[i] === '9') ||
      (!restrictLength && i >= mask.length)
    ) {
      parsedValue += cleanValue[cleanValueIndex];
      cleanValueIndex++;
    } else {
      parsedValue += mask[i];
    }
  }

  return parsedValue;
};

export const handleIdentificationNumber = (
  value: string,
  country: string,
  identificationType: string,
  setFieldValue: (field: string, value: string) => void,
) => {
  const maskData =
    ID_NUMBER_MASK_MAP[
      identificationType || DEFAULT_IDENTIFICATION_TYPE_FOR_COUNTRY[country]
    ];

  const allowLetters = maskData ? maskData.allowLetters || false : true;

  const cleanValue = cleanString(value, allowLetters);

  const mask = maskData?.[cleanValue.length] || maskData?.defaultMask;

  if (!mask) {
    setFieldValue('identificationNumberParsed', value);
    setFieldValue('identificationNumber', cleanValue);
    return;
  }

  const parsedValue = fitToMask(cleanValue, mask, false);

  setFieldValue('identificationNumberParsed', parsedValue);
  setFieldValue('identificationNumber', cleanString(parsedValue, allowLetters));
};

export const handleCardNumber = (
  value: string,
  setFieldValue: (field: string, value: string) => void,
) => {
  const cleanValue = cleanString(value, false);

  if (cleanValue.length > 20) {
    return;
  }

  setFieldValue(
    'cardNumber',
    fitToMask(cleanValue, '9999 9999 9999 9999 9999', true),
  );
};
